import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlots,
} from '@mui/x-data-grid';
import {
  randomId,
} from '@mui/x-data-grid-generator';
import { CustomGridToolbarProps, ResponseProps } from '../../../@types/types';
import GamesOperations from '../../../InfraStructure/Games/GamesOperations';
import { useDispatch, useSelector } from 'react-redux';
import Toast from '../../../Common/Helpers/Toast';
import { CircularProgress } from '@mui/material';
import { successToast } from '../../../Common/Helpers/Utils';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import { MultipleSelect } from '../../../Components/Medias';

interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
  ) => void;
}

type DataProps = {
  data: GridRowsProp;
  onSave: (newRow: GridRowModel) => Promise<ResponseProps>;
  onDelete: (id: GridRowId) => Promise<boolean>;
};

export default function CrudQuestions({ data, onSave, onDelete }: DataProps) {
  const [rows, setRows] = React.useState(data);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [mode, setMode] = React.useState<'view' | 'edit' | 'new'>('view');
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState('');
  // eslint-disable-next-line
  const [loading, setLoading] = React.useState({ id: undefined, loading: false });

  const dispatch = useDispatch();
  const { IsLoading, Data: Games } = useSelector(
    (state: any) => state.games
  );

  function EditToolbar(props: EditToolbarProps) {
    const { setRows, setRowModesModel } = props;
  
    const handleClick = () => {
      const id = randomId();
      setRows((oldRows) => {
        const newRows = [{
        id,
        cdInternet: '',
        idJogo: 0,
        pergunta: '',
        geral: 0,
        isNew: true,
      }, ...oldRows];
        return newRows;
      });
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'pergunta' },
      }));

      setMode('new');
    };
  
    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={mode !== 'view'}
          onClick={handleClick}>
          Adicionar
        </Button>
      </GridToolbarContainer>
    );
  }

  const handleValidationData = (newRow: GridRowModel) => {
    const { idJogo, pergunta } = newRow;
    
    if (idJogo.length < 1) {
      return { isValid: false, message: 'Selecione pelo menos um jogo.' };
    }
    if (pergunta.length < 3) {
      return { isValid: false, message: 'Informe a pergunta.' };
    }

    return { isValid: true, message: '' };
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    setMode('view');
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    if (mode === 'edit') {
      setMessage('Não é possível editar mais de um registro por vez.');
      setOpen(true);
      return;
    }
    setMode('edit');
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    Swal.fire({
      title: 'Atenção',
      text: 'Deseja mesmo apagar esta pergunta?',
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, apagar!',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading({ id, loading: true });
        const response = await onDelete(id);
        if (response === true) {
          successToast('Pergunta apagada com sucesso!');
          setRows(rows.filter((row) => row.id !== id));
          setLoading({ id, loading: false });
        } else {
          setOpen(true);
          setMessage('Erro ao deletar FlashCard.');
          setLoading({ id, loading: false });
        }
      }
    });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setMode('view');
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };

    if (newRow.isNew) {
      const validation = handleValidationData(newRow);

      if (!validation.isValid) {
        setMessage(validation.message);
        setOpen(true);
        return;
      }

      setLoading({ id: newRow.id, loading: true });
      const response = await onSave(newRow);

      if (response.status === 'success') {
        setMode('view');
        successToast(response.message);
        const returnedRow = {
          ...newRow,
          id: response.data.id,
          created_at: response.data.created_at,
          isNew: false,
        }
        setRows(rows.map((row) => (row.id === newRow.id ? returnedRow : row)));
        setLoading({ id: newRow.id, loading: false });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return returnedRow;
      }
    } else {
      setLoading({ id: newRow.id, loading: true });

      if (newRow.pergunta.length < 3) {
        setMessage('Informe a pergunta.');
        setOpen(true);
        return;
      }

      const response = await onSave(updatedRow);

      if (response.status === 'success') {
        setMode('view');
        successToast(response.message);
        setLoading({ id: newRow.id, loading: false });
        return updatedRow;
      }

      setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
      return updatedRow;
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleVerifyEditMode = (params: any) => {
    if (mode === 'edit') {
      setMessage('Não é possível editar mais de um registro por vez.');
      setOpen(true);

      handleCancelClick(params.id)();
      setMode('edit');
      return;
    } else {
      setMode('edit');
    }
  }

  const getGames = async () => {
    try {
      dispatch<any>(GamesOperations.getGames());
    } catch (error: string | unknown) {
      console.log('Erro ao buscar jogos: ', error);
    }
  };

  React.useEffect(() => {
    setRows(data);
  }, [data]);

  React.useEffect(() => {
    getGames();
    // eslint-disable-next-line
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'id',
      editable: false,
    },
    {
      field: 'cdInternet',
      minWidth: 150,
      flex: 1,
      type: 'string',
      headerAlign: 'center',
      align: 'left',
      editable: false,
      renderHeader: () => <strong>Escola</strong>,
    },
    {
      field: 'idJogo',
      minWidth: 150,
      flex: 1,
      type: 'custom',
      headerAlign: 'center',
      align: IsLoading ? 'center' : 'left',
      editable: true,
      renderHeader: () => <strong>Jogo</strong>,
      renderCell(params) {
        if (IsLoading) {
          return <CircularProgress size={20} />;
        }
        
        const game = Games?.find((game) => game.id === params.value);
        return game?.nome || '';
      },
      renderEditCell(params) {
        if (IsLoading) {
          return <CircularProgress size={20} />;
        }
        
        const fieldParams = {
          ...params,
          items: Games,
          field: 'idJogo',
          nome: 'nome',
        }
        return <MultipleSelect params={fieldParams} />;
      },
    },
    {
      field: 'pergunta',
      minWidth: 200,
      flex: 1,
      type: 'string',
      editable: true,
      renderHeader: () => <strong>Pergunta</strong>,
    },
    {
      field: 'geral',
      minWidth: 80,
      flex: 1,
      type: 'boolean',
      headerAlign: 'center',
      align: 'center',
      editable: true,
      renderHeader: () => <strong style={{ textAlign: 'center'}}>Visível<br /> para todos</strong>,
    },
    {
      field: 'created_at',
      headerAlign: 'center',
      type: 'string',
      minWidth: 180,
      flex: 1,
      editable: true,
      align: 'center',
      renderHeader: () => <strong>Criada em</strong>,
      renderCell(params) {
        const date = format(new Date(params.value), 'dd-MM-yyyy');
        return date;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      renderHeader: () => <strong>Ações</strong>,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <Toast
        message={message}
        open={open}
        setclose={setOpen}
        severity="error"
      />
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        disableColumnMenu
        onRowDoubleClick={handleVerifyEditMode}
        autosizeOptions={{
          columns: ['cdInternet', 'idJogo', 'geral', 'created_at'],
          includeOutliers: true,
          includeHeaders: true,
        }}
        columnVisibilityModel={{
          id: false,
          created_at: mode === 'view',
          cdInternet: false,
        }}
        localeText={{
          noRowsLabel: 'Nenhuma pergunta cadastrada ainda.',
          footerRowSelected: (count) => `${count} perguntas(s) selecionada(s)`,
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: EditToolbar as unknown as GridSlots['toolbar'],
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel } as CustomGridToolbarProps,
          pagination: {
            labelRowsPerPage: "Perguntas por página",
            labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
          }
        }}
      />
    </Box>
  );
}